import React from 'react';
import styled from 'styled-components';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faSpinnerThird,
  faCircleExclamation,
} from '@fortawesome/pro-light-svg-icons';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { IndicatorDetailSectionProps } from './utils';
import { spinAnimation } from 'shared/components/LoadingSpinner';

interface IndicatorBottomButtonsProps {
  requestState: IndicatorDetailSectionProps['requestState'];
  disabledBack?: boolean;
  disabledNext?: boolean;
  nextButtonText?: string;
  previousButtonText?: string;
  onNext: () => unknown;
  onPrevious: () => unknown;
}

const StyledDiv = styled.div`
  margin-top: ${SPACERS.large};
  .buttons-state-wrapper {
    display: flex;
    justify-content: center;
    .previous-button {
      margin-right: ${SPACERS.tiny};
    }
    .next-button {
      margin-left: ${SPACERS.tiny};
    }
    .saved-button {
    }
  }
  .error-button {
    margin-bottom: ${SPACERS.small};
  }
`;

const buttonStyles = `
  width: 120px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
`;

const StyledBase = styled.div<{ isButton: boolean }>`
  ${({ isButton }) => (isButton ? buttonStyles : '')}
  svg {
    margin-right: ${SPACERS.extraTiny};
  }
`;

const StyledSaving = styled(StyledBase)<{ isButton: boolean }>`
  ${({ isButton }) =>
    isButton ? `background-color: ${COLORS.grayLight}` : ''};
  .spinner {
    animation: ${spinAnimation} 1s linear infinite;
  }
`;

const StyledSaved = styled(StyledBase)<{ isButton: boolean }>`
  color: ${COLORS.success};
  ${({ isButton }) => (isButton ? `border: 1px solid ${COLORS.success};` : '')}
  svg path {
    fill: ${COLORS.success};
  }
`;

const StyledError = styled(StyledBase)<{ isButton: boolean }>`
  color: ${COLORS.critical};
  ${({ isButton }) => (isButton ? `border: 1px solid ${COLORS.critical};` : '')}
  svg path {
    fill: ${COLORS.critical};
  }
`;

interface ButtonProps {
  isButton: boolean;
  text?: string;
  className?: string;
}

export const SavingButton: React.FC<ButtonProps> = ({
  text = 'Saving',
  isButton,
  className,
}) => {
  return (
    <StyledSaving
      isButton={isButton}
      className={className}
    >
      <FontAwesomeIcon
        icon={faSpinnerThird}
        className="spinner"
      />
      {text}
    </StyledSaving>
  );
};

export const SavedButton: React.FC<ButtonProps> = ({
  text = 'Saved',
  isButton,
  className,
}) => {
  return (
    <StyledSaved
      isButton={isButton}
      className={className}
    >
      <FontAwesomeIcon icon={faCheck} />
      {text}
    </StyledSaved>
  );
};

export const ErrorButton: React.FC<ButtonProps> = ({
  text = 'Error',
  isButton,
  className,
}) => {
  return (
    <StyledError
      isButton={isButton}
      className={className}
    >
      <FontAwesomeIcon icon={faCircleExclamation} />
      {text}
    </StyledError>
  );
};

const ButtonStates: React.FC<IndicatorBottomButtonsProps> = ({
  requestState,
  disabledBack,
  disabledNext,
  previousButtonText = 'Back',
  nextButtonText = 'Next',
  onPrevious,
  onNext,
}) => {
  switch (requestState) {
    case 'saving':
      return <SavingButton isButton={true} />;
    case 'saved':
      return <SavedButton isButton={true} />;

    default:
      return (
        <>
          <TrueImpactButton
            disabled={disabledBack}
            variant="secondary-large"
            onClick={onPrevious}
            className="previous-button"
          >
            {previousButtonText}
          </TrueImpactButton>
          <TrueImpactButton
            disabled={disabledNext}
            onClick={onNext}
            className="next-button"
          >
            {nextButtonText}
          </TrueImpactButton>
        </>
      );
  }
};

const IndicatorBottomButtons: React.FC<IndicatorBottomButtonsProps> = (
  props,
) => {
  return (
    <StyledDiv>
      {props.requestState === 'error' && (
        <ErrorButton
          text="Unable to save changes, please try again."
          isButton={false}
          className="error-button"
        />
      )}
      <div className="buttons-state-wrapper">
        <ButtonStates {...props} />
      </div>
    </StyledDiv>
  );
};

export default IndicatorBottomButtons;
